import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useReferentialStore } from '@/presentation/stores/referential/referential.ts'
import { UserGender } from '@/core/domain/resources/enums/UserGender.ts'
import { UserRole } from '@/core/domain/resources/enums/UserRole.ts'
import { ClassroomDegree } from '@/core/domain/resources/enums/ClassroomDegree.ts'

export const useGenderOptions = computed(() => {
  return Object.keys(UserGender.Values)
    .filter((key): key is keyof typeof UserGender.Values => key in UserGender.Values)
    .map((genderKey) => ({
      name: UserGender.label(UserGender.Values[genderKey]),
      code: genderKey
    }))
})

export const useRoleOptions = computed(() => {
  return Object.keys(UserRole.Values)
    .filter((key): key is keyof typeof UserRole.Values => key in UserRole.Values)
    .map((roleKey) => ({
      name: UserRole.label(UserRole.Values[roleKey]),
      code: roleKey
    }))
})

export const useBackOfficeRoleOptions = computed(() => {
  return Object.keys(UserRole.BackOfficeValues)
    .filter((key): key is keyof typeof UserRole.BackOfficeValues => key in UserRole.BackOfficeValues)
    .map((roleKey) => ({
      name: UserRole.label(UserRole.BackOfficeValues[roleKey]),
      code: roleKey
    }))
})

export const useSchoolRoleOptions = computed(() => {
  return Object.keys(UserRole.SchoolValues)
    .filter((key): key is keyof typeof UserRole.SchoolValues => key in UserRole.SchoolValues)
    .map((roleKey) => ({
      name: UserRole.label(UserRole.SchoolValues[roleKey]),
      code: roleKey
    }))
})

export const useDegreeOptions = computed(() => {
  return Object.keys(ClassroomDegree.Values)
    .filter((key): key is keyof typeof ClassroomDegree.Values => key in ClassroomDegree.Values)
    .map((degreeKey) => ({
      name: ClassroomDegree.label(ClassroomDegree.Values[degreeKey]),
      code: degreeKey
    }))
})

export const useAxisesOptions = computed(() => {
  const { referential } = storeToRefs(useReferentialStore())
  return referential.value.map((axis) => ({
    name: axis.title,
    code: axis.id
  }))
})

export type DataOption = {
  label: string
  value: string
}

// Create a generic function to convert an enum to data options
export const generateDataOptionsFromEnum = <T extends { [key: string]: string | number }>(
  enumObj: T,
  labelFunc?: (value: string | number) => string
): DataOption[] => {
  return Object.values(enumObj).map((value) => ({
    label: labelFunc ? labelFunc(value) : value.toString(),
    value: value as string
  }))
}
